import React from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import { useMutation } from '@apollo/client';

import { useLocation } from 'react-router-dom';
import MyAccountNavItem from './MyAccountNavItem';
import useUser from 'core/hooks/useUser';
import Header from 'core/ui/components/Header/Header';
import LogoutButton from 'core/ui/components/LogoutButton';
import { EXTERNAL_LINKS } from 'core/api/constants';
import { Box, Text, RouterLink } from 'core/ui/atoms';
import { NavItem , MobileNavItem } from 'core/ui/atoms/V2';
import { isGuestEmail } from 'onramp/helpers';
import client from 'core/api/apollo/apolloClient';
import { SIGN_OUT } from 'core/api/apollo/mutations';
import { goToInitOrder } from 'core/api/actions/navigation';
import { session } from 'core/api/storage';
import useReferralCode from 'core/hooks/useReferralCode';

import { ReactComponent as CartIcon } from 'core/assets/images/V2/icons/CartIcon.svg';
import { ReactComponent as UserSVG } from 'core/assets/images/V2/icons/User.svg';
import { ReactComponent as PlusIcon } from 'core/assets/images/V2/icons/PlusIcon.svg';

function OnrampHeader() {
  const { currentUser } = useUser();
  const { clearReferralCode } = useReferralCode();
  const location = useLocation();

  const onLogoutComplete = () => {
    session.clear();
    return client.resetStore().then(()=>{
      clearReferralCode();
      goToInitOrder();
    });
  };

  const [logout] = useMutation(SIGN_OUT, {
    variables: { input: {} },
    onCompleted: onLogoutComplete,
    onError: onLogoutComplete,
  });

  const isMyOrderPage = location.pathname === '/onramp/my-order';

  const MyOrderNavigation = () => {
    if (isGuestEmail(currentUser?.email)) return null;

    if (isEmpty(currentUser?.subscription?.subscriptionQuantities)) {
      return (
        <CartNavigation to='/onramp/my-order' tabIndex={-1} aria-hidden={true}>
          <CartContainer>
            <StyledCartIcon $dimmed={isMyOrderPage} />
          </CartContainer>
        </CartNavigation>
      );
    }
    return null;
  };

  const MobileLoginOptions = () => {
    if (!isEmpty(currentUser?.email) && isGuestEmail(currentUser?.email)) {
      return <MobileNavItem to="/sign-in" key="nav-sign-in">Log In</MobileNavItem>;
    } else {
      return (
        <MobileNavItem key="nav-programs"
          additionalComponent={
            <LogoutButton reload={true} textButton={true}>Log Out</LogoutButton>
          }
          subMenus={[
            { label: 'Weekly Menu', href: '/onramp/order-management' },
            { label: 'My Program', href: '/onramp/my-program' },
            { label: 'My Settings', href: '/onramp/my-settings' },
            { label: 'My Orders', href: '/onramp/order-history' },
            { label: 'My Invoices', href: '/onramp/my-invoices' },
            { label: 'Contact', href: '/onramp/order-management' },
          ]}>
          My Account
        </MobileNavItem>
      );
    }
  };

  const MyAccountNavItemComponent = () => {
    if (currentUser && !isEmpty(currentUser.email) && !isGuestEmail(currentUser.email)) {
      return (
        <MyAccountNavItem
          key="nav-my-account"
          showMenuHeader={true}
          onLogoutClick={logout}
          subMenus={[
            { label: 'Weekly Menu', href: '/onramp/order-management' },
            { label: 'My Program', href: '/onramp/my-program' },
            { label: 'My Settings', href: '/onramp/my-settings' },
            { label: 'My Orders', href: '/onramp/order-history' },
            { label: 'Log Out', href: '#', isLogout: true, component: <LogoutButton reload={true}>Log Out</LogoutButton> },
          ]}>
          My Account <PlusIcon />
          <MyOrderNavigation />
        </MyAccountNavItem>
      );
    } else {
      return (
        <NavItem to="/sign-in" activeClassName='hidden' key="nav-sign-in">
          Log-In <SVGIcon />
        </NavItem>
      );
    }
  };

  const rightNavItems = () => {
    return [
      <MyAccountNavItem key="nav-programs"
        subMenus={[{ label: 'Signature Program', href: EXTERNAL_LINKS.signatureProgram, target: '_blank' },
                  ]}>
        Our Programs <PlusIcon />
      </MyAccountNavItem>,
      <NavItem href={EXTERNAL_LINKS.ourStory} key="nav-our-story" target='_blank'>Our Story</NavItem>,
      <MyAccountNavItemComponent key='my-account' />,
    ];
  };

  const WelcomeNavItem = () => {
    if (currentUser && !isEmpty(currentUser.email) && !isGuestEmail(currentUser.email)) {
      return (<NameTitle>Welcome, {currentUser?.firstName} {currentUser?.lastName}</NameTitle>);
    }
    return [];
  };

  const mobileNavItems = () => {
    return [
      <WelcomeNavItem key="user-name" />,
      <MobileNavItem href='/onramp/init-order' key="nav-our-story">Home</MobileNavItem>,
      <MobileNavItem key="nav-programs"
        subMenus={[{ label: 'Signature Program', href: EXTERNAL_LINKS.signatureProgram, target: '_blank' },
                  ]}>
        Our Programs
      </MobileNavItem>,
      <MobileNavItem href={EXTERNAL_LINKS.ourStory} key="nav-our-story" target='_blank'>
        Our Story
      </MobileNavItem>,
      <MobileLoginOptions key='nav-account-option' />,
    ];
  };

  return (
    <Header rightNavItems={rightNavItems} mobileNavItems={mobileNavItems} />
  );
}

const CartNavigation = styled(RouterLink)`
`;

const StyledCartIcon = styled(CartIcon)`
  opacity: ${(p) => (p.$dimmed ? 0.5 : 1)};
`;

const CartContainer = styled(Box)`
  margin-right: 5px;
  margin-top: 5px;
`;

const SVGIcon = styled(UserSVG)`
  vertical-align: middle;
  margin-left: 0.3em;
  height: 28px;
  width: 28px;
`;

const NameTitle = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-weight: 400;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: 0%;
  font-style: italic;
  color: ${({ theme }) => theme.colors.white};
`;

export default OnrampHeader;
